import React, { useCallback, useEffect, useState } from "react";
import {
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiX,
} from "react-icons/fi";
import Header from "../components/layout/Header";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Select from "../components/ui/Select";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";
import { expenseReportsApi, ExpenseReport } from "../api/expenseReports";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import toast from "react-hot-toast";

const STATUS_VARIANT: Record<
  ExpenseReport["status"],
  "warning" | "success" | "danger" | "neutral"
> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "neutral",
};

const FILTER_OPTIONS = [
  { value: "pending", label: "En attente" },
  { value: "", label: "Toutes" },
  { value: "approved", label: "Validées" },
  { value: "rejected", label: "Refusées" },
  { value: "cancelled", label: "Annulées" },
];

const ExpenseReportsRhPage: React.FC = () => {
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ExpenseReport | null>(null);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject">(
    "approve",
  );
  const [rhComment, setRhComment] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await expenseReportsApi.list({
        status: statusFilter || undefined,
        per_page: 100,
      });
      setReports(res.data.data?.reports ?? []);
      setPendingCount(res.data.data?.pending_count ?? 0);
    } catch {
      toast.error("Impossible de charger les notes de frais");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openReview = (report: ExpenseReport, action: "approve" | "reject") => {
    setReviewTarget(report);
    setReviewAction(action);
    setRhComment("");
  };

  const handleReview = async () => {
    if (!reviewTarget) return;
    setReviewing(true);
    try {
      await expenseReportsApi.review(reviewTarget.id, {
        action: reviewAction,
        rh_comment: rhComment.trim() || undefined,
      });
      toast.success(
        reviewAction === "approve" ? "Note validée" : "Note refusée",
      );
      setReviewTarget(null);
      await loadData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Échec du traitement";
      toast.error(message);
    } finally {
      setReviewing(false);
    }
  };

  const handleDownloadReceipt = async (
    reportId: number,
    lineId: number,
    filename?: string | null,
  ) => {
    try {
      const res = await expenseReportsApi.downloadReceipt(reportId, lineId);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "justificatif";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  const pendingTotal = reports
    .filter((r) => r.status === "pending")
    .reduce((sum, r) => sum + r.total_amount, 0);

  return (
    <div>
      <Header
        title="Notes de frais"
        subtitle="Validez les déclarations de frais de vos collaborateurs"
      />

      <div style={{ padding: "2rem", maxWidth: 1100, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            {pendingCount > 0 && (
              <Badge variant="warning">{pendingCount} en attente</Badge>
            )}
            {statusFilter === "pending" && pendingTotal > 0 && (
              <span
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-text-secondary)",
                }}
              >
                Total :{" "}
                {pendingTotal.toLocaleString("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                })}
              </span>
            )}
          </div>
          <div style={{ minWidth: 220 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={FILTER_OPTIONS}
            />
          </div>
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "3rem",
            }}
          >
            <LoadingSpinner size={36} />
          </div>
        ) : reports.length === 0 ? (
          <Card padding="2rem">
            <EmptyState
              title="Aucune note de frais"
              description={
                statusFilter === "pending"
                  ? "Aucune note en attente de validation."
                  : "Aucune note ne correspond à ce filtre."
              }
            />
          </Card>
        ) : (
          <Card padding="0">
            {reports.map((report, index) => (
              <div
                key={report.id}
                style={{
                  borderTop:
                    index > 0
                      ? "1px solid var(--color-border-light)"
                      : undefined,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr 100px 100px 120px",
                    gap: "0.5rem",
                    alignItems: "center",
                    padding: "1rem 1.25rem",
                  }}
                >
                  <div
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                    onClick={() =>
                      setExpandedId(expandedId === report.id ? null : report.id)
                    }
                  >
                    {expandedId === report.id ? (
                      <FiChevronUp size={16} />
                    ) : (
                      <FiChevronDown size={16} />
                    )}
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 500,
                          fontSize: "var(--font-size-sm)",
                        }}
                      >
                        {report.title}
                      </p>
                      <p
                        style={{
                          margin: "0.1rem 0 0",
                          fontSize: "var(--font-size-xs)",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        {report.employee_name || report.user_name}
                        {" · "}
                        {format(new Date(report.created_at), "d MMM yyyy", {
                          locale: fr,
                        })}
                      </p>
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: "var(--font-size-sm)",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    {report.lines_count} ligne
                    {report.lines_count > 1 ? "s" : ""}
                  </span>
                  <span
                    style={{ fontWeight: 600, fontSize: "var(--font-size-sm)" }}
                  >
                    {report.total_amount.toLocaleString("fr-FR", {
                      style: "currency",
                      currency: "EUR",
                    })}
                  </span>
                  <Badge variant={STATUS_VARIANT[report.status]} size="sm">
                    {report.status_label}
                  </Badge>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "0.35rem",
                    }}
                  >
                    {report.status === "pending" ? (
                      <>
                        <button
                          type="button"
                          title="Valider"
                          onClick={() => openReview(report, "approve")}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--color-success)",
                            cursor: "pointer",
                          }}
                        >
                          <FiCheck size={18} />
                        </button>
                        <button
                          type="button"
                          title="Refuser"
                          onClick={() => openReview(report, "reject")}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--color-danger)",
                            cursor: "pointer",
                          }}
                        >
                          <FiX size={18} />
                        </button>
                      </>
                    ) : (
                      <span
                        style={{
                          fontSize: "var(--font-size-xs)",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        {" "}
                        -{" "}
                      </span>
                    )}
                  </div>
                </div>
                {expandedId === report.id && report.lines && (
                  <div
                    style={{
                      padding: "0 1.25rem 1rem 2.5rem",
                      background: "var(--color-bg)",
                    }}
                  >
                    {report.comment && (
                      <p
                        style={{
                          margin: "0 0 0.5rem",
                          fontSize: "var(--font-size-xs)",
                          color: "var(--color-text-secondary)",
                        }}
                      >
                        Commentaire : « {report.comment} »
                      </p>
                    )}
                    {report.lines.map((line) => (
                      <div
                        key={line.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0.4rem 0",
                          fontSize: "var(--font-size-sm)",
                          borderTop: "1px solid var(--color-border-light)",
                          gap: "0.75rem",
                        }}
                      >
                        <span>
                          <strong>{line.category_label}</strong> :{" "}
                          {line.description}
                          <span style={{ color: "var(--color-text-muted)" }}>
                            {" "}
                            ({format(new Date(line.expense_date), "dd/MM/yyyy")}
                            )
                          </span>
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            flexShrink: 0,
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>
                            {line.amount.toLocaleString("fr-FR", {
                              style: "currency",
                              currency: "EUR",
                            })}
                          </span>
                          {line.has_receipt && (
                            <button
                              type="button"
                              title="Justificatif"
                              onClick={() =>
                                handleDownloadReceipt(
                                  report.id,
                                  line.id,
                                  line.receipt_filename,
                                )
                              }
                              style={{
                                background: "none",
                                border: "none",
                                color: "var(--color-brand)",
                                cursor: "pointer",
                              }}
                            >
                              <FiDownload size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}
      </div>

      <Modal
        isOpen={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        title={
          reviewAction === "approve"
            ? "Valider la note de frais"
            : "Refuser la note de frais"
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setReviewTarget(null)}>
              Annuler
            </Button>
            <Button onClick={handleReview} disabled={reviewing}>
              {reviewing
                ? "Traitement…"
                : reviewAction === "approve"
                  ? "Valider"
                  : "Refuser"}
            </Button>
          </>
        }
      >
        {reviewTarget && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <p style={{ margin: 0, fontSize: "var(--font-size-sm)" }}>
              <strong>
                {reviewTarget.employee_name || reviewTarget.user_name}
              </strong>
              {" - "}
              {reviewTarget.total_amount.toLocaleString("fr-FR", {
                style: "currency",
                currency: "EUR",
              })}
              {" ("}
              {reviewTarget.lines_count} ligne
              {reviewTarget.lines_count > 1 ? "s" : ""})
            </p>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.35rem",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: 500,
                }}
              >
                Commentaire RH (optionnel)
              </label>
              <textarea
                value={rhComment}
                onChange={(e) => setRhComment(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "0.625rem 0.875rem",
                  fontSize: "var(--font-size-sm)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  fontFamily: "var(--font-family)",
                }}
                placeholder={
                  reviewAction === "reject"
                    ? "Motif du refus…"
                    : "Message pour le collaborateur…"
                }
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ExpenseReportsRhPage;
